<template>
  <div class="overlay-container pointer-events-none">
    <div class="overflow-hidden" :class="`tile-row-size-${tileRowSize}`" :style="`--tileInset: ${TILE_INSET}px`">
      <PortalTarget name="hotspot-modal" />
      <PortalTarget name="deck-border" />
      <transition name="fade">
        <div
          v-if="state.contentOverlay"
          class="backdrop-blur absolute top-0 left-0 h-full w-full pointer-events-auto overflow-auto"
          data-testid="content-overlay"
        />
      </transition>
      <p v-if="state.slideTitle" class="mt-8 text-center text-white text-xl">{{ slideTitle }}</p>
      <div class="overlay gap-1">
        <div class="grid-mode-portal">
          <transition name="fade">
            <div v-if="state.gridMode" class="flex justify-center items-center w-full h-full">
              <PortalTarget name="grid-mode" />
            </div>
          </transition>
        </div>
        <div v-if="state.screenShare" class="screen-share-portal mx-auto px-3">
          <PortalTarget name="screen-share" />
        </div>
        <div class="top-row absolute w-full flex justify-between px-5" data-testid="top-row" :class="topRowHeight">
          <div class="top-left flex">
            <div v-if="state.siteBackNavigation" class="site-back-btn portal w-min pointer-events-auto mr-6">
              <PortalTarget name="site-back-btn" />
            </div>
            <div v-if="state.hotspotBack" class="three-d-hotspot-back-btn portal w-min pointer-events-auto mr-6">
              <PortalTarget name="three-d-hotspot-back-btn" />
            </div>
          </div>
          <div class="top-right">
            <DeckControlsContainer v-if="state.exitDeck" :class="['pointer-events-auto', { 'hidden sm_block': isSidebarOpen }]">
              <DeckOverlayExitDeck @click="$emit('exit-deck')" />
            </DeckControlsContainer>
          </div>
        </div>
        <div class="portal absolute bottom-8 left-4">
          <PortalTarget name="deck-overlay-vrify-logo">
            <DeckOverlayVrifyLogo
              v-if="state.vrifyLogo"
              :larger-logo-breakpoint="isSidebarOpen ? 'lg' : 'sm'"
              :hide-on-mobile="isMeeting"
            />
          </PortalTarget>
        </div>
        <div class="controls-row grid-cols-3 px-5 pb-5 w-full grid justify-items-stretch items-center pointer-events-none">
          <div v-if="state.threeDLegends" class="portal col-span-full" :class="[isSidebarOpen ? 'lg_flex' : 'sm_flex']">
            <PortalTarget name="deck-controls-three-d-legends" />
          </div>

          <div
            v-if="state.hostAnnotation"
            class="deck-controls-annotations col-start-1 justify-self-start hidden"
            :class="[isSidebarOpen ? 'lg_flex' : 'sm_flex']"
          >
            <div class="flex flex-grow">
              <PortalTarget name="deck-controls-annotations" />
            </div>
          </div>
          <div v-if="state.meetingControls" class="col-start-2 col-end-3 flex justify-self-center">
            <div class="flex flex-grow">
              <PortalTarget name="meeting-controls" />
            </div>
          </div>
          <div class="col-end-4 flex items-end justify-self-end">
            <DeckControlsContainer v-if="state.fullscreen" class="mr-8 flex-grow">
              <DeckOverlayEmbeddedFullscreenBtn class="pointer-events-auto" @click="$emit('toggle-fullscreen')" />
            </DeckControlsContainer>
            <div
              v-if="state.annotation"
              class="portal mr-8 flex-grow pointer-events-auto hidden deck-controls-annotations"
              :class="[isSidebarOpen ? 'lg_flex' : 'sm_flex']"
            >
              <PortalTarget name="deck-controls-annotations" :slot-props="{ tooltipDirection: 'left' }" />
            </div>
            <div
              v-if="state.threeDLayersMenu"
              class="portal mr-8 flex-grow pointer-events-auto hidden deck-controls-three-d-layer"
              :class="[isSidebarOpen ? 'lg_flex' : 'sm_flex']"
            >
              <PortalTarget name="deck-controls-three-d-layer" />
            </div>
            <DeckOverlayArrowNavigation
              v-if="state.arrowNavigation"
              class="pointer-events-auto"
              :deck="deck"
              :current-slide-index="currentSlideIndex"
              :first-valid-slide="firstValidSlide"
              :last-valid-slide="lastValidSlide"
              :analytics-event-name="analyticsEventName"
              :deck-user-type="deckUserType"
              :embed-deck="embedDeck"
              @navigation-previous-slide="$emit('navigation-previous-slide')"
              @navigation-next-slide="$emit('navigation-next-slide')"
            />
          </div>
          <div class="three-d-error-modal">
            <PortalTarget name="three-d-error-modal" />
          </div>
        </div>
        <DeckOverlaySidebarToggle
          v-if="state.sidebarToggle"
          class="pointer-events-auto"
          :is-sidebar-open="isSidebarOpen"
          @click="$emit('toggle-sidebar')"
        />
      </div>

      <div class="overlay p-3 gap-3 pointer-events-none" style="--tileInset: 0px">
        <PortalTarget v-if="state.avTileRow" name="av-tile-host">
          <template #default="nodes">
            <transition name="fade">
              <component :is="nodes?.[0]" />
            </transition>
          </template>
        </PortalTarget>

        <PortalTarget v-if="state.avTileRow" name="av-tile-participants">
          <template #default="nodes">
            <transition name="fade">
              <component :is="nodes?.[0]" />
            </transition>
          </template>
        </PortalTarget>

        <PortalTarget v-if="state.avTileRow" name="av-tile-local">
          <template #default="nodes">
            <transition name="fade">
              <component :is="nodes?.[0]" />
            </transition>
          </template>
        </PortalTarget>

        <div v-if="state.attendeePresence" class="participant-toast-portal">
          <PortalTarget name="participant-toast" multiple>
            <template #default="nodes">
              <transition-group name="fade">
                <component :is="nodes" v-for="node in nodes" :key="node" />
              </transition-group>
            </template>
          </PortalTarget>
        </div>
      </div>
    </div>
    <DeckEmbeddedSplashScreen
      v-if="state.splashScreen"
      class="pointer-events-auto"
      @dismiss-embed-overlay="$emit('dismiss-embed-overlay')"
    />
  </div>
</template>

<script lang="ts" setup>
import type { DeepReadonly, PropType } from 'vue'
import type { Deck, IncludeCompany, DeckUserType } from '@/entities/deck'
import type { HotspotType, SiteChangeEvent } from '@/entities/site'
import { useFullscreenHelpers } from '@/composables/useFullscreenHelpers'
import { useDeckOverlayState } from '@/composables/useDeckOverlayState'
import { TILE_INSET } from '@/composables/useTileRowSpace'
import type { TileRowSize } from '@/composables/useTileRowSize'
import type { DeckWithEmbed } from '@/entities/embed'

const props = defineProps({
  isSidebarOpen: {
    type: Boolean,
    required: true,
  },
  deck: {
    type: Object as PropType<DeepReadonly<Deck & IncludeCompany>>,
    required: true,
  },
  currentSlideIndex: {
    type: Number,
    required: true,
  },
  firstValidSlide: {
    type: Number,
    required: true,
  },
  lastValidSlide: {
    type: Number,
    required: true,
  },
  analyticsEventName: {
    type: String,
    required: true,
  },
  embedDeck: {
    type: Object as PropType<DeepReadonly<DeckWithEmbed>>,
    default: null,
  },
  isMeeting: {
    type: Boolean,
    required: true,
  },
  isAvSession: {
    type: Boolean,
    required: true,
  },
  isContentBeingShared: {
    type: Boolean,
    required: true,
  },
  isGridMode: {
    type: Boolean,
    required: true,
  },
  isAutoPlaying: {
    type: Boolean,
    required: true,
  },
  siteHotspotType: {
    type: String as PropType<HotspotType | ''>,
    required: true,
  },
  deckUserType: {
    type: String as PropType<DeckUserType>,
    default: '',
  },
  siteHistory: {
    type: Array as PropType<DeepReadonly<SiteChangeEvent[]>>,
    default: () => [],
  },
  tileRowSize: {
    type: String as PropType<TileRowSize>,
    default: 'small',
  },
  isSlideTransitioning: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits([
  'toggle-sidebar',
  'dismiss-embed-overlay',
  'navigation-previous-slide',
  'navigation-next-slide',
  'toggle-fullscreen',
  'exit-deck',
])

const { isFullscreenEnabled } = useFullscreenHelpers({ deck: toRef(props, 'deck') })

const { state } = useDeckOverlayState({
  embedType: computed(() => props.embedDeck?.embed_type),
  isFullscreenEnabled,
  isSidebarOpen: toRef(props, 'isSidebarOpen'),
  deckUserType: toRef(props, 'deckUserType'),
  currentSlideIndex: toRef(props, 'currentSlideIndex'),
  deck: toRef(props, 'deck'),
  isMeeting: toRef(props, 'isMeeting'),
  isAvSession: toRef(props, 'isAvSession'),
  isContentBeingShared: toRef(props, 'isContentBeingShared'),
  isGridMode: toRef(props, 'isGridMode'),
  isAutoPlaying: toRef(props, 'isAutoPlaying'),
  isVRIFYLogoHidden: computed(() => props.deck.company.is_vrify_logo_hidden),
  isLayerMenuShowing: computed(() => props.deck.show_3d_menu),
  isSlideTransitioning: toRef(props, 'isSlideTransitioning'),
  siteHotspotType: toRef(props, 'siteHotspotType'),
  siteHistory: toRef(props, 'siteHistory'),
})

const topRowHeight = computed(() => {
  if (!state.value.avTileRow) return 'top-5'
  return (
    {
      xl: 'top-54',
      lg: 'top-47',
      md: 'top-42',
      sm: 'top-38',
      xs: 'top-34',
    }[props.tileRowSize] || 'top-34'
  )
})

const slideTitle = computed(() => props.deck.slides[props.currentSlideIndex].title)
</script>

<style lang="stylus" scoped>
.tile-row-size-xs
  --tileSize 109px

.tile-row-size-sm
  --tileSize 125px

.tile-row-size-md
  --tileSize 144px

.tile-row-size-lg
  --tileSize 165px

.tile-row-size-xl
  --tileSize 192px

.overlay
  @apply absolute inset-0 h-full w-full grid
  grid-template-rows calc(var(--tileSize) + var(--tileInset)) minmax(0, 1fr) auto
  grid-template-columns auto minmax(0, 1fr) auto

/* slightly transparent fallback */
.backdrop-blur
  background-color rgba(255, 255, 255, .9)

/* if backdrop support */
@supports ((-webkit-backdrop-filter: none) or (backdrop-filter: none))
  .backdrop-blur
    background-color transparent
    @apply backdrop-filter backdrop-blur-3xl

.grid-mode-portal
  @apply w-full h-full pt-3 px-3

  grid-row 1 / -2
  grid-column 1 / -1
  & > *
    @apply flex justify-center items-center w-full h-full

.screen-share-portal
  grid-row-start 2
  grid-column 1 / span 3
  & > *
    @apply flex justify-center items-center w-full h-full

.participant-toast-portal
  grid-column span 2 / 4
  align-self start
  justify-self end

:deep(.local-tile)
  grid-column-start 3

.controls-row
  grid-row-start 3
  grid-column 1 / span 3

.fade-enter-active, .fade-leave-active
  @apply transition-opacity duration-300

.fade-enter, .fade-leave-to
  @apply opacity-0

$moving-time = .5s
$fading-time = .5s
$fade-then-move = $moving-time $fading-time
$move-then-fade = $fading-time .2s
$move-linear = $moving-time linear

:deep(.toast-item)
  overflow hidden
  max-height 5rem
  transition opacity $fading-time, max-height $fade-then-move, padding-top $fade-then-move, padding-bottom $fade-then-move, margin $fade-then-move
  &.fade-enter-active
    transition opacity $move-then-fade, max-height $move-linear, padding-top $move-linear, padding-bottom $move-linear, margin $move-linear
  &.fade-enter, &.fade-leave-to
    opacity 0
    max-height 0
    padding-top 0 !important
    padding-bottom 0 !important
</style>
